import React from 'react';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingHeading from 'coaching/components/CoachingHeading';
import Columns2 from 'components/Columns2';
import Copy from 'components/Copy';
import Display from 'components/Display';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Radio from 'components/Radio';
import Row from 'components/Row';
import Select from 'components/Select';
import { Student } from 'src/coaching/containers/GetProspectiveStudentOptionsContainer';
import Text from 'components/Text';

type Props = {
  students?: Student[];
  subjects?: string[];
  gradeLevels?: string[];
  loading: boolean;
  loadingError?: string;
  selectedSubject?: string;
  onSelectSubject: (subject: string) => void;
  onSelectStudent: (studentUuid: string) => void;
  onSelectGradeLevel: (gradeLevel?: string) => void;
  selectedGradeLevel?: string;
  selectedStudentUuid?: string;
  manualInputChildName?: string;
  onChangeName: (name?: string) => void;
  onContinue: () => void;
  disabledContinueButton: boolean;
};

type State = {
  showAddChild: boolean;
};

export default class CoachingSelfServeStudentDetailsView extends React.Component<Props, State> {
  state: State = {
    showAddChild: false,
  };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.students && this.props.students) {
      // only track the view event after the students have been loaded
      this.trackEvent('view');
    }
  }

  trackEvent(key: string) {
    const prefix =
      this.props.students && this.props.students.length > 0
        ? 'student_details_connected'
        : 'student_details_unconnected';
    EventTracking.trackKey(`coaching.self_schedule_purchase.${prefix}.${key}`);
  }

  showAndClearManualChildNameInput = () => {
    this.trackEvent('add_new_child.click');
    this.props.onChangeName('');
    this.setState({ showAddChild: true });
  };

  hideManualChildNameInput = () => {
    this.setState({ showAddChild: false });
  };

  renderStudents = (students: Student[]) => {
    const { manualInputChildName, onChangeName, selectedStudentUuid, onSelectStudent } = this.props;
    const { showAddChild } = this.state;
    const manualChildNameInputField = (
      <FormElement
        data-test="student-name-field"
        onValidChange={e => onChangeName(e.target.value)}
        onInvalidChange={() => onChangeName(undefined)}
        validate={e => assertions.firstAndLastName(e.target.value)}
        label="Child's name"
        element={Input}
        elementProps={{
          defaultValue: manualInputChildName,
          placeholder: 'First and last name',
          onClick: () => this.trackEvent('add_new_child_input.click'),
        }}
      />
    );

    if (students.length === 0) {
      return manualChildNameInputField;
    }

    const addChildRadio = (
      <Radio
        data-test="add-child-radio"
        label="Add a child"
        name={undefined}
        onChange={this.showAndClearManualChildNameInput}
        checked={showAddChild}
      />
    );

    return (
      <Row>
        {students.map(student => {
          const { name, uuid } = student;
          return (
            <Radio
              data-test={uuid}
              key={uuid}
              label={name}
              name={uuid}
              checked={selectedStudentUuid === uuid}
              onChange={e => {
                this.trackEvent('existing_child.click');
                onSelectStudent(e.currentTarget.name);
                this.hideManualChildNameInput();
              }}
            />
          );
        })}
        {showAddChild ? (
          <React.Fragment>
            <Row>{addChildRadio}</Row>
            {manualChildNameInputField}
          </React.Fragment>
        ) : (
          addChildRadio
        )}
      </Row>
    );
  };

  render() {
    const {
      subjects,
      students,
      gradeLevels,
      onSelectSubject,
      selectedSubject,
      onContinue,
      disabledContinueButton,
      selectedGradeLevel,
      onSelectGradeLevel,
      loading,
      loadingError,
    } = this.props;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    const errorText = 'Oops, looks like something went wrong - please try refreshing the page.';
    if (loadingError) {
      return (
        <ErrorReporter
          error={`CoachingSelfServeStudentDetailsView - could not load subjects e=${loadingError}`}>
          {errorText}
        </ErrorReporter>
      );
    } else if (!subjects || !gradeLevels || !students) {
      return (
        <ErrorReporter
          error={`CoachingSelfServeStudentDetailsView - could not load subjects, grade level, or students. 
          subjects=${subjects} gradeLevel=${gradeLevels} students=${students}`}>
          {errorText}
        </ErrorReporter>
      );
    }

    const subjectOptions = subjects.map(s => ({ label: s, value: s }));
    const gradeLevelOptions = gradeLevels.map(s => ({ label: s, value: s }));

    const subjectDropdown = (
      <FormElement
        data-test="subject-dropdown"
        onValidChange={e => {
          this.trackEvent('subjects.click');
          onSelectSubject(e);
        }}
        label="Math subject"
        element={Select}
        elementProps={{
          options: subjectOptions,
          placeholder: 'Select subject',
          value: selectedSubject,
        }}
      />
    );

    const gradeLevelDropdown = (
      <FormElement
        data-test="gradeLevel-dropdown"
        onValidChange={e => {
          this.trackEvent('gradeLevel.click');
          onSelectGradeLevel(e);
        }}
        label="Grade Level"
        element={Select}
        elementProps={{
          options: gradeLevelOptions,
          placeholder: 'Select grade level',
          value: selectedGradeLevel,
        }}
      />
    );

    return (
      <React.Fragment>
        <CoachingHeading>Tell us a few details</CoachingHeading>
        <Row>
          <Copy align="left" size="small">
            We’ll use this information to select a Remind coach who’s uniquely suited to working
            with your child. Currently, we focus on{' '}
            <Text weight="semibold">K–12 math subjects.</Text>
          </Copy>
        </Row>
        <Row size="xsmall">
          {this.renderStudents(students)}
          <Display show="480">
            {gradeLevelDropdown}
            {subjectDropdown}
          </Display>
          <Display hide="480">
            <Columns2>
              <div>{gradeLevelDropdown}</div>
              <div>{subjectDropdown}</div>
            </Columns2>
          </Display>
        </Row>
        <Button
          data-test="student-details-continue-button"
          color="koolaid"
          onClick={() => {
            this.trackEvent('continue.click');
            onContinue();
          }}
          disabled={disabledContinueButton}>
          Continue
        </Button>
      </React.Fragment>
    );
  }
}
